import { BASE_URL} from "../config/busApiConfig";

// Simple headers — no token needed, backend handles it
const headers = { "Content-Type": "application/json" };

export async function fetchCities() {
  const res = await fetch(`${BASE_URL}/cities`, { headers });
  if (!res.ok) throw new Error("Failed to fetch cities");
  
  return res.json();
}

export async function searchBuses({ sourceId, destinationId, doj }) {
  const res = await fetch(`${BASE_URL}/search`, {
    method: "POST",
    headers,
    body: JSON.stringify({ sourceId, destinationId, doj }),
  });
  if (!res.ok) throw new Error("Search failed");
  return res.json();
}

export async function fetchSeatLayout({ tripId, searchTrackId, doj }) {
  const res = await fetch(`${BASE_URL}/seat-layout`, {  // ← was hardcoded "/api/bus/seat-layout"
    method: "POST",
    headers,
    body: JSON.stringify({ tripId, searchTrackId, doj }),
  });
  if (!res.ok) throw new Error("Seat layout fetch failed");
  return res.json();
}

export async function repriceBus({ searchTrackId, id, bpId, dpId, selectedSeats }) {
  const res = await fetch(`${BASE_URL}/reprice`, {
    method: "POST",
    headers,
    body: JSON.stringify({ searchTrackId, id, bpId, dpId, selectedSeats }),
  });
  if (!res.ok) throw new Error("Reprice failed");
  return res.json();
}

// busApi.js — fix blockTicket to use BASE_URL like every other function

export async function blockTicket(params) {
  const res = await fetch(`${BASE_URL}/block-ticket`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      requestId:        params.requestId,        // ✅ now forwarded — required by the API
      busId:            params.busId,
      bpId:             params.bpId,
      dpId:             params.dpId,
      searchTrackId:    params.searchTrackId,
      passengerDetails: params.passengerDetails,
      contactDetails:   params.contactDetails,
      gstNo:            params.gstNo ?? "",
      callbackUrl:      params.callbackUrl,
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = body?.detail ?? body?.message ?? JSON.stringify(body);
    throw new Error(`Block ticket failed (${res.status}): ${detail}`);
  }
  return res.json();
}
export async function bookTicket({ blockKey }) {
  const res = await fetch(`${BASE_URL}/book-ticket`, {
    method: "POST",
    headers,
    body: JSON.stringify({ blockKey }),
  });
  
  if (!res.ok) throw new Error("Booking failed");
  return res.json();
}

export async function fetchBookingHistory({ fromDate, toDate, pnr, bookingId, customerMobile, currentPage = 1, perPage = 10 } = {}) {
  const params = new URLSearchParams({ currentPage, perPage });
  if (fromDate) params.append("fromDate", fromDate);
  if (toDate)   params.append("toDate", toDate);
  if (pnr)      params.append("pnr", pnr);
  if (bookingId) params.append("bookingId", bookingId);
  if (customerMobile) params.append("customerMobile", customerMobile);

  const res = await fetch(`${BASE_URL}/booking-history?${params.toString()}`, { headers });
  if (!res.ok) throw new Error("Failed to fetch booking history");
  return res.json();
}

export async function fetchBookingDetails(bookingId) {
  const res = await fetch(`${BASE_URL}/booking-details/${bookingId}`, { headers });
  if (!res.ok) throw new Error("Failed to fetch booking details");
  return res.json();
}

// Generates a unique requestId per call — required by Cancellation Data / Cancel Ticket APIs
function generateRequestId(prefix = "R") {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
}

export async function fetchCancellationData({ bookingId, seatsToCancel, requestId }) {
  const res = await fetch(`${BASE_URL}/cancellation-data`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      requestId: requestId ?? generateRequestId("CD"),
      bookingId,
      seatsToCancel,
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = body?.statusDetail?.message ?? body?.detail ?? body?.message ?? JSON.stringify(body);
    throw new Error(`Failed to fetch cancellation data (${res.status}): ${detail}`);
  }
  return res.json();
}

export async function cancelTicket({ bookingId, seatNos, seatsToCancel, requestId }) {
  const res = await fetch(`${BASE_URL}/cancel-ticket`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      requestId: requestId ?? generateRequestId("CT"),
      bookingId,
      seatsToCancel: seatsToCancel || seatNos,
    }),
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = body?.statusDetail?.message ?? body?.detail ?? body?.message ?? JSON.stringify(body);
    throw new Error(`Cancellation failed (${res.status}): ${detail}`);
  }
  return res.json();
}
export async function getBalance() {
  const res = await fetch(`${BASE_URL}/get-balance`, { headers });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = body?.statusDetail?.message ?? body?.detail ?? body?.message ?? JSON.stringify(body);
    throw new Error(`Failed to fetch balance (${res.status}): ${detail}`);
  }
  const data = await res.json();
  return data.availableBalance;
}
